// Cache global compartilhado entre useAuth, useEmpresaContext e usePermissoes
// Arquivo separado para evitar dependência circular

// Interface local para evitar dependência circular
interface PermissaoMenuCache {
  id: string
  empresa_id: string
  perfil: string
  menu_id: string
  submenu_id: string | null
  ativo: boolean
}

// Chaves do localStorage para persistência
const STORAGE_KEYS = {
  SOFTWARE_HOUSE: 'vision_software_house',
  SOFTWARE_HOUSE_EMPRESA_ID: 'vision_software_house_empresa_id',
  USUARIO_EMPRESA_ID: 'vision_usuario_empresa_id',
  USUARIO_ID: 'vision_usuario_id'
}

// Funções auxiliares para localStorage
const getFromStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const setToStorage = (key: string, value: string | null) => {
  try {
    if (value === null) {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, value)
    }
  } catch {
    // Silently fail if localStorage is not available
  }
}

// Cache para empresa_id do usuário logado (com fallback para localStorage)
let globalUsuarioEmpresaId: string | null = getFromStorage(STORAGE_KEYS.USUARIO_EMPRESA_ID)
let globalUsuarioId: string | null = getFromStorage(STORAGE_KEYS.USUARIO_ID)

// Cache para modo software_house (com fallback para localStorage)
let globalSoftwareHouse: boolean = getFromStorage(STORAGE_KEYS.SOFTWARE_HOUSE) === 'true'
let globalSoftwareHouseEmpresaId: string | null = getFromStorage(STORAGE_KEYS.SOFTWARE_HOUSE_EMPRESA_ID)

// Cache para empresa
let globalEmpresaId: string | null = null
let globalEmpresaPromise: Promise<string | null> | null = null

// Cache para permissões de menu
let globalPermissoes: PermissaoMenuCache[] = []

// Funções para gerenciar cache do usuário
export const getUsuarioEmpresaId = (): string | null => globalUsuarioEmpresaId
export const getUsuarioLogadoId = (): string | null => globalUsuarioId

export const setUsuarioEmpresaId = (empresaId: string | null) => {
  globalUsuarioEmpresaId = empresaId
  setToStorage(STORAGE_KEYS.USUARIO_EMPRESA_ID, empresaId)
}

export const setUsuarioLogadoId = (usuarioId: string | null) => {
  globalUsuarioId = usuarioId
  setToStorage(STORAGE_KEYS.USUARIO_ID, usuarioId)
}

export const clearUsuarioCache = () => {
  globalUsuarioEmpresaId = null
  globalUsuarioId = null
  setToStorage(STORAGE_KEYS.USUARIO_EMPRESA_ID, null)
  setToStorage(STORAGE_KEYS.USUARIO_ID, null)
}

// Funções para gerenciar cache do software_house
export const isSoftwareHouse = (): boolean => {
  // Re-ler do localStorage para garantir valor atualizado
  const fromStorage = getFromStorage(STORAGE_KEYS.SOFTWARE_HOUSE) === 'true'
  if (fromStorage !== globalSoftwareHouse) {
    globalSoftwareHouse = fromStorage
  }
  return globalSoftwareHouse
}

export const setSoftwareHouse = (value: boolean) => {
  globalSoftwareHouse = value
  setToStorage(STORAGE_KEYS.SOFTWARE_HOUSE, value ? 'true' : null)
}

export const getSoftwareHouseEmpresaId = (): string | null => {
  // Re-ler do localStorage para garantir valor atualizado
  const fromStorage = getFromStorage(STORAGE_KEYS.SOFTWARE_HOUSE_EMPRESA_ID)
  if (fromStorage !== globalSoftwareHouseEmpresaId) {
    globalSoftwareHouseEmpresaId = fromStorage
  }
  return globalSoftwareHouseEmpresaId
}

export const setSoftwareHouseEmpresaId = (empresaId: string | null) => {
  globalSoftwareHouseEmpresaId = empresaId
  setToStorage(STORAGE_KEYS.SOFTWARE_HOUSE_EMPRESA_ID, empresaId)
}

// Retorna o empresa_id efetivo (considerando software_house)
export const getEfetiveEmpresaId = (): string | null => {
  // Sempre re-ler do localStorage para garantir valores atualizados após reload
  const isSH = getFromStorage(STORAGE_KEYS.SOFTWARE_HOUSE) === 'true'
  const shEmpresaId = getFromStorage(STORAGE_KEYS.SOFTWARE_HOUSE_EMPRESA_ID)
  const usuarioEmpresaId = getFromStorage(STORAGE_KEYS.USUARIO_EMPRESA_ID)

  // Atualizar variáveis globais se necessário
  if (isSH !== globalSoftwareHouse) globalSoftwareHouse = isSH
  if (shEmpresaId !== globalSoftwareHouseEmpresaId) globalSoftwareHouseEmpresaId = shEmpresaId
  if (usuarioEmpresaId !== globalUsuarioEmpresaId) globalUsuarioEmpresaId = usuarioEmpresaId

  if (isSH && shEmpresaId) {
    return shEmpresaId
  }

  return usuarioEmpresaId
}

export const clearSoftwareHouseCache = () => {
  globalSoftwareHouse = false
  globalSoftwareHouseEmpresaId = null
  setToStorage(STORAGE_KEYS.SOFTWARE_HOUSE, null)
  setToStorage(STORAGE_KEYS.SOFTWARE_HOUSE_EMPRESA_ID, null)
}

// Funções para gerenciar cache da empresa
export const getGlobalEmpresaId = (): string | null => globalEmpresaId
export const setGlobalEmpresaId = (empresaId: string | null) => {
  globalEmpresaId = empresaId
}

export const getGlobalEmpresaPromise = (): Promise<string | null> | null => globalEmpresaPromise
export const setGlobalEmpresaPromise = (promise: Promise<string | null> | null) => {
  globalEmpresaPromise = promise
}

export const clearEmpresaCache = () => {
  globalEmpresaId = null
  globalEmpresaPromise = null
}

// Funções para gerenciar cache de permissões
export const getPermissoes = (): PermissaoMenuCache[] => globalPermissoes
export const setPermissoes = (perms: PermissaoMenuCache[]) => {
  globalPermissoes = perms
}
export const clearPermissoes = () => {
  globalPermissoes = []
}

// Limpar todos os caches (logout)
export const clearAllCache = () => {
  clearUsuarioCache()
  clearEmpresaCache()
  clearPermissoes()
  clearSoftwareHouseCache()
}
